import { usePathname } from 'next/navigation';
import React, { FC } from 'react';
import { IoCalendarOutline } from 'react-icons/io5';

import { Box, BoxProps, Tooltip } from '@mantine/core';
import { BoothHelpers } from 'src/helpers/BoothHelpers';

import { CompanyProfile, Event } from 'models';
import { EventHelpers } from 'helpers/EventHelpers';
import { TextHelpers } from 'helpers/TextHelpers';

import { useCompanyBooth } from 'hooks/use-company-booth';

import cx from './BoothNumber.module.scss';

type BoothNumberProps = {
    booth: string;
    event?: string;
    hall?: string;
    url?: string;
} & BoxProps;

const WrappedBoothNumber: FC<
    {
        event?: Event;
        company: Pick<CompanyProfile, 'name' | 'id'>;
        showEventName?: boolean;
    } & BoxProps
> = ({ event, company, showEventName, ...rest }) => {
    const { event: activeEvent, booth } = useCompanyBooth(company, event);
    const pathname = usePathname();

    if (!booth) {
        return null;
    }

    const eventUrl = EventHelpers.urls.view(activeEvent.slug);
    const { hallName } = BoothHelpers.getHall(booth, activeEvent.boothPatterns, activeEvent.name);

    return (
        <BoothNumber
            booth={booth}
            event={showEventName ? activeEvent.name : ''}
            hall={hallName === activeEvent.name ? undefined : hallName}
            url={pathname === eventUrl ? undefined : eventUrl}
            {...rest}
        />
    );
};

const BoothNumber: FC<BoothNumberProps> = ({ booth, event, hall, url, ...rest }) => {
    const Body = (
        <Box
            className={`gradient-cyan-green-background-light ${cx.root}`}
            {...rest}
            component={url ? 'a' : 'span'}
            href={url}
        >
            <IoCalendarOutline size={16} />

            {event && <span className={cx.event}>{TextHelpers.getTextWithEllipsis(event, 20)}</span>}
            <span className={cx.booth}>{booth}</span>
        </Box>
    );

    return (
        <Tooltip label={hall ? `Find us at booth ${booth} in ${hall}` : `Find us at booth ${booth}`}>{Body}</Tooltip>
    );
};

export { WrappedBoothNumber as BoothNumber };

const MapYourShowBoothNumber: FC<Pick<BoothNumberProps, 'booth'> & BoxProps> = ({ booth, ...rest }) => {
    const mapUrl = BoothHelpers.getMapUrl(booth) ?? '#';

    return <BoothNumber booth={booth} url={mapUrl} {...rest} />;
};

export { MapYourShowBoothNumber };
